/* Plushington Allure — theme scripts */
(function () {
  'use strict';

  /* Chrome pauses muted "video-only" playback with an AbortError on tabs that
     are backgrounded (power-saving) — e.g. a product link opened in a new
     background tab, or the browser window minimized right as a slide changes.
     Retry once when the tab actually becomes visible, so the video still
     starts the moment the shopper looks at it instead of staying silently
     stuck forever. */
  function playVideoWhenVisible(video) {
    video.play().catch(function () {
      if (!document.hidden) return;
      var onVisible = function () {
        if (document.hidden) return;
        document.removeEventListener('visibilitychange', onVisible);
        video.play().catch(function () {});
      };
      document.addEventListener('visibilitychange', onVisible);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initHeaderScroll();
    initMobileDrawer();
    initMobileNavDrilldown();
    initMegaMenuPosition();
    initSlideshows();
    initTabs();
    initCountdowns();
    initBeforeAfter();
    initTimelines();
    initQuantitySelectors();
    initCarouselNav();
    initPreselectVariantLinks();
    initJudgemeWriteReview();
    initReviewFilters();
    initReviewSort();
    initReviewLightbox();
    initJudgemeFullReviews();
    initProductGallery();
    initLightbox();
    initStickyAddBar();
    initCollectionFilters();
    initCardSliders();
    initFeatureCardVideos();
    initCardSwatches();
    initShopSwatches();
    initLocalizationSelect();
    initWelcomePopup();
    syncNewsletterJoinedFromServerState();
    initSizeGuideModal();

    document.addEventListener('plushington:content-injected', function (e) {
      initCardSliders(e.detail && e.detail.root);
    });
  });

  /* ---------- Header scroll shrink ---------- */
  function initHeaderScroll() {
    var header = document.querySelector('.site-header');
    if (!header) return;
    var ticking = false;
    function update() {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
      ticking = false;
    }
    update();
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
  }

  /* ---------- Desktop mega-menu: pin it to the header's real edges ----------
     .site-nav__item already needs position:relative for its own underline/
     chevron, so it's the nearest positioned ancestor an absolutely-positioned
     panel would resolve against — not what an edge-to-edge mega-menu wants.
     position:fixed sidesteps that ancestor chain entirely; reading the
     header's real bounding rect keeps the panel pixel-exact regardless of
     viewport width or the scrolled/unscrolled header state. */
  function initMegaMenuPosition() {
    var header = document.querySelector('.site-header');
    var menus = document.querySelectorAll('.mega-menu');
    if (!header || !menus.length) return;

    function position() {
      var rect = header.getBoundingClientRect();
      menus.forEach(function (menu) {
        menu.style.left = rect.left + 'px';
        menu.style.width = rect.width + 'px';
        menu.style.top = rect.bottom + 'px';
      });
    }
    position();
    window.addEventListener('resize', position);
    document.querySelectorAll('.site-nav__item--has-dropdown').forEach(function (item) {
      item.addEventListener('mouseenter', position);
    });
  }

  /* ---------- Mobile drawer ---------- */
  function initMobileDrawer() {
    var toggle = document.querySelector('[data-drawer-open]');
    var drawer = document.querySelector('[data-drawer]');
    if (!toggle || !drawer) return;
    var closeButtons = drawer.querySelectorAll('[data-drawer-close]');

    toggle.addEventListener('click', function () {
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      toggle.setAttribute('aria-expanded', 'true');
    });

    function resetNavPanels() {
      var root = drawer.querySelector('.mobile-drawer__panel--root');
      if (root) root.classList.remove('is-pushed');
      drawer.querySelectorAll('.mobile-drawer__panel:not(.mobile-drawer__panel--root)').forEach(function (p) {
        p.classList.remove('is-active');
      });
    }

    closeButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        drawer.classList.remove('is-open');
        document.body.style.overflow = '';
        toggle.setAttribute('aria-expanded', 'false');
        setTimeout(resetNavPanels, 350);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        drawer.classList.remove('is-open');
        document.body.style.overflow = '';
        toggle.setAttribute('aria-expanded', 'false');
        setTimeout(resetNavPanels, 350);
      }
    });
  }

  /* ---------- Mobile nav drill-down: tapping a top-level item with real
     content slides to its own sub-panel (with a back button) instead of
     expanding in place — same box, root and sub-panel swapped via
     translateX so the mechanics are pure CSS transitions, not layout
     reflow. ---------- */
  function initMobileNavDrilldown() {
    var container = document.querySelector('[data-mobile-nav]');
    if (!container) return;
    var root = container.querySelector('.mobile-drawer__panel--root');
    if (!root) return;

    container.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-nav-open]');
      if (openBtn) {
        var target = container.querySelector('[data-nav-panel="' + openBtn.dataset.navOpen + '"]');
        if (!target) return;
        root.classList.add('is-pushed');
        target.classList.add('is-active');
        return;
      }
      var backBtn = e.target.closest('[data-nav-back]');
      if (backBtn) {
        var panel = backBtn.closest('.mobile-drawer__panel');
        if (panel) panel.classList.remove('is-active');
        root.classList.remove('is-pushed');
      }
    });
  }

  /* ---------- Slideshow ---------- */
  function initSlideshows() {
    document.querySelectorAll('[data-slideshow]').forEach(function (slideshow) {
      var track = slideshow.querySelector('[data-slideshow-track]');
      var slides = track ? Array.from(track.children) : [];
      var dots = slideshow.querySelectorAll('[data-slideshow-dot]');
      var prevBtn = slideshow.querySelector('[data-slideshow-prev]');
      var nextBtn = slideshow.querySelector('[data-slideshow-next]');
      if (!track || slides.length < 2) return;

      var index = 0;
      var autoplay = slideshow.dataset.autoplay === 'true';
      var interval = parseInt(slideshow.dataset.interval || '6', 10) * 1000;
      var timer = null;

      function goTo(i) {
        index = (i + slides.length) % slides.length;
        track.style.transform = 'translateX(-' + index * 100 + '%)';
        slides.forEach(function (slide, si) {
          slide.classList.toggle('is-active', si === index);
        });
        dots.forEach(function (dot, di) {
          dot.classList.toggle('is-active', di === index);
        });
      }

      dots.forEach(function (dot, di) {
        dot.addEventListener('click', function () {
          goTo(di);
          restart();
        });
      });

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          goTo(index - 1);
          restart();
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          goTo(index + 1);
          restart();
        });
      }

      /* Touch swipe */
      var startX = 0;
      var endX = 0;
      slideshow.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
      }, { passive: true });

      slideshow.addEventListener('touchend', function (e) {
        endX = e.changedTouches[0].clientX;
        var diff = startX - endX;
        if (Math.abs(diff) > 45) {
          if (diff > 0) {
            goTo(index + 1);
          } else {
            goTo(index - 1);
          }
          restart();
        }
      }, { passive: true });

      /* Pause autoplay on hover */
      slideshow.addEventListener('mouseenter', function () {
        if (autoplay) clearInterval(timer);
      });
      slideshow.addEventListener('mouseleave', function () {
        restart();
      });

      function restart() {
        if (!autoplay) return;
        clearInterval(timer);
        timer = setInterval(function () { goTo(index + 1); }, interval);
      }

      goTo(0);
      restart();
    });
  }

  /* ---------- Tabs (featured collection, generic) ---------- */
  function initTabs() {
    document.querySelectorAll('[data-tabs]').forEach(function (root) {
      var tabs = root.querySelectorAll('[data-tab]');
      var panels = root.querySelectorAll('[data-tab-panel]');
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var id = tab.dataset.tab;
          tabs.forEach(function (t) {
            t.classList.toggle('is-active', t === tab);
            t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
          });
          panels.forEach(function (panel) {
            panel.hidden = panel.dataset.tabPanel !== id;
          });
        });
      });
    });
  }

  /* ---------- Countdown ---------- */
  function initCountdowns() {
    document.querySelectorAll('[data-countdown]').forEach(function (el) {
      var end = new Date(el.dataset.countdown).getTime();
      if (isNaN(end)) return;
      var fields = {
        days: el.querySelector('[data-cd-days]'),
        hours: el.querySelector('[data-cd-hours]'),
        mins: el.querySelector('[data-cd-mins]'),
        secs: el.querySelector('[data-cd-secs]')
      };
      function tick() {
        var diff = Math.max(0, end - Date.now());
        var d = Math.floor(diff / 86400000);
        var h = Math.floor(diff / 3600000) % 24;
        var m = Math.floor(diff / 60000) % 60;
        var s = Math.floor(diff / 1000) % 60;
        if (fields.days) fields.days.textContent = String(d).padStart(2, '0');
        if (fields.hours) fields.hours.textContent = String(h).padStart(2, '0');
        if (fields.mins) fields.mins.textContent = String(m).padStart(2, '0');
        if (fields.secs) fields.secs.textContent = String(s).padStart(2, '0');
      }
      tick();
      setInterval(tick, 1000);
    });
  }

  /* ---------- Before / after ---------- */
  function initBeforeAfter() {
    document.querySelectorAll('[data-before-after]').forEach(function (root) {
      var range = root.querySelector('[data-ba-range]');
      var after = root.querySelector('.before-after__after');
      var handle = root.querySelector('.before-after__handle');
      if (!range || !after || !handle) return;
      range.addEventListener('input', function () {
        var v = range.value;
        after.style.clipPath = 'inset(0 0 0 ' + v + '%)';
        handle.style.left = v + '%';
      });
    });
  }

  /* ---------- Timeline year switcher ---------- */
  function initTimelines() {
    document.querySelectorAll('[data-timeline]').forEach(function (root) {
      var years = root.querySelectorAll('[data-timeline-year]');
      var panels = root.querySelectorAll('[data-timeline-panel]');
      years.forEach(function (year) {
        year.addEventListener('click', function () {
          var id = year.dataset.timelineYear;
          years.forEach(function (y) { y.classList.toggle('is-active', y === year); });
          panels.forEach(function (p) { p.hidden = p.dataset.timelinePanel !== id; });
        });
      });
    });
  }

  /* ---------- Quantity selector ---------- */
  function initQuantitySelectors() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qty-change]');
      if (!btn) return;
      var input = btn.parentElement.querySelector('input[type="number"]');
      if (!input) return;
      var step = btn.dataset.qtyChange === 'up' ? 1 : -1;
      var next = Math.max(parseInt(input.min || '1', 10), (parseInt(input.value, 10) || 1) + step);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- Product gallery (thumbs + main slides) ---------- */
  function initProductGallery() {
    var gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-thumb]'));
    var slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-slide]'));
    var main = gallery.querySelector('[data-gallery-main]');
    var prevBtn = gallery.querySelector('[data-gallery-prev]');
    var nextBtn = gallery.querySelector('[data-gallery-next]');
    var counterCurrent = gallery.querySelector('[data-gallery-counter-current]');
    var total = slides.length;
    var current = 0;

    function pad(n) { return (n < 9 ? '0' : '') + (n + 1); }

    function goTo(index) {
      current = ((index % total) + total) % total;
      thumbs.forEach(function (t) { t.classList.toggle('is-active', t.dataset.index === String(current)); });
      slides.forEach(function (s) {
        var i = parseInt(s.dataset.index, 10);
        var isActive = i === current;
        s.classList.toggle('is-active', isActive);
        var offset = i - current;
        if (offset > total / 2) offset -= total;
        if (offset < -total / 2) offset += total;
        s.style.transform = 'translateX(' + (offset * 100) + '%)';

        var video = s.querySelector('video');
        if (video) {
          if (isActive) {
            video.currentTime = 0;
            playVideoWhenVisible(video);
          } else {
            video.pause();
          }
        }
      });
      if (counterCurrent) counterCurrent.textContent = pad(current);
      var activeThumb = thumbs[current];
      var thumbsRail = gallery.querySelector('[data-gallery-thumbs]');
      /* Scroll only the thumb rail's own scrollTop, not element.scrollIntoView() —
         scrollIntoView walks every scrollable ancestor, and combined with the
         sticky gallery column that reliably nudges the outer page by a few
         pixels on desktop (the "unrelated" info column looked unstable, but
         it was really the whole viewport shifting under a sticky element). */
      if (activeThumb && thumbsRail) {
        var railRect = thumbsRail.getBoundingClientRect();
        var thumbRect = activeThumb.getBoundingClientRect();
        if (thumbRect.top < railRect.top || thumbRect.bottom > railRect.bottom) {
          var delta = thumbRect.top < railRect.top
            ? thumbRect.top - railRect.top
            : thumbRect.bottom - railRect.bottom;
          thumbsRail.scrollBy({ top: delta, behavior: 'smooth' });
        }
      }
    }

    function goToMediaPosition(position) {
      if (!position) return false;
      var targetIndex = -1;
      slides.forEach(function (s, i) {
        if (String(s.dataset.mediaPosition) === String(position)) targetIndex = i;
      });
      if (targetIndex === -1) return false;
      goTo(targetIndex);
      return true;
    }
    window.theme = window.theme || {};
    window.theme.gallery = { goTo: goTo, goToMediaPosition: goToMediaPosition };

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () { goTo(parseInt(thumb.dataset.index, 10)); });
    });
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current + 1); });

    if (total > 1 && main) {
      main.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1); }
      });

      var startX = 0, startY = 0, dragging = false, moved = false;
      main.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        dragging = true;
        moved = false;
        startX = e.clientX;
        startY = e.clientY;
      });
      main.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var dx = e.clientX - startX, dy = e.clientY - startY;
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) moved = true;
      });
      main.addEventListener('pointerup', function (e) {
        if (!dragging) return;
        dragging = false;
        var dx = e.clientX - startX, dy = e.clientY - startY;
        if (moved && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          goTo(current + (dx < 0 ? 1 : -1));
        }
      });
      main.addEventListener('pointercancel', function () { dragging = false; });

      // A drag that ends as a swipe shouldn't also trigger the zoom-lightbox click.
      main.addEventListener('click', function (e) {
        if (moved) {
          e.stopPropagation();
          e.preventDefault();
          moved = false;
        }
      }, true);
    }

    goTo(0);
  }

  /* ---------- Lightbox ---------- */
  function initLightbox() {
    var lightbox = document.querySelector('[data-lightbox]');
    var gallery = document.querySelector('[data-product-gallery]');
    if (!lightbox || !gallery) return;

    var img = lightbox.querySelector('[data-lightbox-img]');
    var counter = lightbox.querySelector('[data-lightbox-counter]');
    var closeBtn = lightbox.querySelector('[data-lightbox-close]');
    var prevBtn = lightbox.querySelector('[data-lightbox-prev]');
    var nextBtn = lightbox.querySelector('[data-lightbox-next]');
    var zoomBtn = document.querySelector('[data-gallery-zoom]');
    var thumbsWrap = lightbox.querySelector('[data-lightbox-thumbs]');

    var sources = Array.from(gallery.querySelectorAll('[data-gallery-slide][data-zoom-src]')).map(function (s) {
      return { src: s.dataset.zoomSrc, index: parseInt(s.dataset.index, 10), mediaPosition: s.dataset.mediaPosition };
    });
    if (!sources.length) return;

    function indexForMediaPosition(position) {
      var found = -1;
      sources.forEach(function (s, i) { if (String(s.mediaPosition) === String(position)) found = i; });
      return found;
    }

    var thumbEls = [];
    var swatchEls = Array.from(document.querySelectorAll('.variant-options--swatch .swatch[data-media-position]'));

    if (thumbsWrap && swatchEls.length > 1) {
      /* Real color/pattern swatches — jump straight to the linked image. */
      swatchEls.forEach(function (sw) {
        var idx = indexForMediaPosition(sw.dataset.mediaPosition);
        if (idx === -1) return;
        var fill = sw.querySelector('.swatch__fill');
        if (!fill) return;
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'lightbox__thumb';
        b.setAttribute('aria-label', sw.getAttribute('title') || 'View this color');
        b.appendChild(fill.cloneNode(true));
        b.dataset.index = idx;
        b.addEventListener('click', function () { show(idx); });
        thumbsWrap.appendChild(b);
        thumbEls.push(b);
      });
    }
    if (thumbsWrap && thumbEls.length === 0 && sources.length > 1) {
      /* Fallback: no color options on this product — plain photo filmstrip. */
      sources.forEach(function (s, i) {
        var rail = document.querySelector('[data-gallery-thumb][data-index="' + s.index + '"] img');
        var thumbSrc = rail ? rail.src : s.src;
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'lightbox__thumb';
        b.setAttribute('aria-label', 'View image ' + (i + 1));
        var im = document.createElement('img');
        im.src = thumbSrc;
        im.loading = 'lazy';
        im.alt = '';
        b.appendChild(im);
        b.dataset.index = i;
        b.addEventListener('click', function () { show(i); });
        thumbsWrap.appendChild(b);
        thumbEls.push(b);
      });
    }

    var imgWrap = lightbox.querySelector('.lightbox__img-wrap');
    var current = 0;
    var scale = 1;
    var panX = 0;
    var panY = 0;
    var MIN_SCALE = 1;      // whole image visible, nothing cropped
    var DEFAULT_SCALE = 1;  // the "zoomed cover" look — recomputed per image below
    var MAX_SCALE = 3;      // multiplied against DEFAULT_SCALE once known

    function applyTransform(animate) {
      img.style.transition = (animate ? 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), ' : '') + 'opacity 0.25s ease';
      img.style.transform = 'scale(' + scale + ') translate(' + panX + 'px, ' + panY + 'px)';
      img.classList.toggle('is-zoomed', scale > MIN_SCALE + 0.01);
    }

    function clampPan() {
      var maxOffset = (scale - MIN_SCALE) * 220;
      panX = Math.max(-maxOffset, Math.min(maxOffset, panX));
      panY = Math.max(-maxOffset, Math.min(maxOffset, panY));
    }

    /* object-fit:contain leaves the image letterboxed at scale 1 — compute
       how much scale is needed to make it fill the frame edge-to-edge (the
       look we want by default), so MIN_SCALE (1) stays reachable as a real
       "zoom out to see the whole photo" state instead of a hard floor. */
    function computeDefaultScale() {
      var ww = imgWrap.clientWidth, wh = imgWrap.clientHeight;
      var nw = img.naturalWidth, nh = img.naturalHeight;
      if (!ww || !wh || !nw || !nh) return false;
      var isMobile = window.matchMedia('(max-width: 700px)').matches;
      if (isMobile) {
        var containScale = Math.min(ww / nw, wh / nh);
        var containW = nw * containScale, containH = nh * containScale;
        DEFAULT_SCALE = Math.max(ww / containW, wh / containH);
      } else {
        /* Desktop: open on the full, uncropped image — plenty of screen
           real estate already, no need to start pre-zoomed like mobile does. */
        DEFAULT_SCALE = MIN_SCALE;
      }
      MAX_SCALE = Math.max(DEFAULT_SCALE * 3, MIN_SCALE * 3);
      return true;
    }

    function resetZoom(animate) {
      scale = DEFAULT_SCALE; panX = 0; panY = 0;
      applyTransform(animate);
    }

    function show(i) {
      current = (i + sources.length) % sources.length;
      img.style.opacity = '0';
      var onLoad = function () {
        img.removeEventListener('load', onLoad);
        if (!computeDefaultScale()) {
          /* Wrap not laid out yet (rare) — one more try next frame. */
          requestAnimationFrame(function () { computeDefaultScale(); finishShow(); });
          return;
        }
        finishShow();
      };
      function finishShow() {
        scale = DEFAULT_SCALE; panX = 0; panY = 0;
        applyTransform(false);
        img.style.opacity = '1';
      }
      img.addEventListener('load', onLoad);
      img.src = sources[current].src;
      if (img.complete && img.naturalWidth) onLoad();
      if (counter) counter.textContent = (current + 1) + ' / ' + sources.length;
      thumbEls.forEach(function (t) {
        var isActive = parseInt(t.dataset.index, 10) === current;
        t.classList.toggle('is-active', isActive);
        if (isActive) t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }

    function open(startIndex) {
      /* Unhide first — the wrap must actually have layout dimensions before
         show() can compute the default cover-scale off of it. Doing this in
         the other order silently fell back to "whole image" for cached
         photos, since their load event (and default-scale calc) could fire
         before display:none had lifted. */
      lightbox.hidden = false;
      document.body.style.overflow = 'hidden';
      show(startIndex || 0);
    }

    function close() {
      lightbox.hidden = true;
      document.body.style.overflow = '';
      resetZoom(false);
    }

    gallery.querySelectorAll('[data-gallery-slide][data-zoom-src]').forEach(function (slide) {
      slide.addEventListener('click', function () {
        var sourceIndex = sources.findIndex(function (s) { return s.index === parseInt(slide.dataset.index, 10); });
        open(sourceIndex >= 0 ? sourceIndex : 0);
      });
      slide.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          slide.click();
        }
      });
    });

    if (zoomBtn) {
      zoomBtn.addEventListener('click', function () {
        var active = gallery.querySelector('[data-gallery-slide].is-active');
        var sourceIndex = active ? sources.findIndex(function (s) { return s.index === parseInt(active.dataset.index, 10); }) : 0;
        open(sourceIndex >= 0 ? sourceIndex : 0);
      });
    }

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (prevBtn) prevBtn.addEventListener('click', function () { show(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { show(current + 1); });
    lightbox.addEventListener('click', function (e) { if (e.target === lightbox) close(); });
    document.addEventListener('keydown', function (e) {
      if (lightbox.hidden) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft' && scale <= DEFAULT_SCALE + 0.01) show(current - 1);
      if (e.key === 'ArrowRight' && scale <= DEFAULT_SCALE + 0.01) show(current + 1);
    });

    /* Double-click / double-tap to zoom — toggles between the default look and a closer view */
    img.addEventListener('dblclick', function () {
      if (scale <= DEFAULT_SCALE + 0.01) { scale = Math.min(MAX_SCALE, DEFAULT_SCALE * 2); }
      else { scale = DEFAULT_SCALE; panX = 0; panY = 0; }
      clampPan();
      applyTransform(true);
    });

    /* Wheel to zoom (desktop) — full range from whole-image (MIN_SCALE) to well past the default */
    img.addEventListener('wheel', function (e) {
      e.preventDefault();
      scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale + (e.deltaY > 0 ? -0.25 : 0.25)));
      if (scale <= MIN_SCALE + 0.01) { panX = 0; panY = 0; }
      clampPan();
      applyTransform(false);
    }, { passive: false });

    /* Touch: swipe to navigate at 1x, drag to pan when zoomed, pinch to zoom */
    var touchStartX = 0, touchStartY = 0, touchStartPanX = 0, touchStartPanY = 0;
    var pinchStartDist = 0, pinchStartScale = 1;
    var isDragging = false;
    var wasMultiTouch = false;

    function touchDist(touches) {
      var dx = touches[0].clientX - touches[1].clientX;
      var dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    lightbox.addEventListener('touchstart', function (e) {
      isDragging = false;
      if (e.touches.length === 2) {
        wasMultiTouch = true;
        pinchStartDist = touchDist(e.touches);
        pinchStartScale = scale;
      } else if (e.touches.length === 1) {
        wasMultiTouch = false;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchStartPanX = panX;
        touchStartPanY = panY;
      }
    }, { passive: true });

    lightbox.addEventListener('touchmove', function (e) {
      if (e.touches.length === 2) {
        e.preventDefault();
        wasMultiTouch = true;
        var dist = touchDist(e.touches);
        scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, pinchStartScale * (dist / pinchStartDist)));
        clampPan();
        applyTransform(false);
      } else if (e.touches.length === 1 && scale > DEFAULT_SCALE + 0.01) {
        e.preventDefault();
        isDragging = true;
        panX = touchStartPanX + (e.touches[0].clientX - touchStartX) / scale;
        panY = touchStartPanY + (e.touches[0].clientY - touchStartY) / scale;
        clampPan();
        applyTransform(false);
      }
    }, { passive: false });

    lightbox.addEventListener('touchend', function (e) {
      if (scale <= DEFAULT_SCALE + 0.01 && !isDragging && !wasMultiTouch && e.changedTouches.length === 1 && e.touches.length === 0) {
        var dx = e.changedTouches[0].clientX - touchStartX;
        var dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          show(dx > 0 ? current - 1 : current + 1);
        }
      }
      if (e.touches.length === 0) {
        if (scale < 1) resetZoom(true);
        isDragging = false;
        wasMultiTouch = false;
      }
    });
  }

  /* ---------- Sticky add-to-cart bar ---------- */
  function initStickyAddBar() {
    var bar = document.querySelector('[data-sticky-bar]');
    var trigger = document.querySelector('[data-product-form] [type="submit"]');
    if (!bar || !trigger) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    observer.observe(trigger);

    var stickyBtn = bar.querySelector('[data-sticky-add-btn]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () { trigger.click(); });
    }
  }

  /* ---------- Collection filters + sort ---------- */
  function initCollectionFilters() {
    var toggles = document.querySelectorAll('[data-filters-toggle]');
    var panel = document.querySelector('[data-filters-panel]');
    var overlay = document.querySelector('[data-filters-overlay]');
    var facetForm = document.getElementById('FacetFiltersForm');

    if (toggles.length && panel && overlay) {
      var isOpen = false;

      function openPanel() {
        isOpen = true;
        overlay.hidden = false;
        panel.hidden = false;
        requestAnimationFrame(function () {
          overlay.classList.add('is-visible');
          panel.classList.add('is-open');
        });
        document.body.style.overflow = 'hidden';
        toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
      }

      function closePanel() {
        isOpen = false;
        overlay.classList.remove('is-visible');
        panel.classList.remove('is-open');
        document.body.style.overflow = '';
        toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
        setTimeout(function () {
          if (!isOpen) { overlay.hidden = true; panel.hidden = true; }
        }, 400);
      }

      toggles.forEach(function (toggle) {
        toggle.addEventListener('click', function () {
          if (panel.classList.contains('is-open')) closePanel(); else openPanel();
        });
      });
      overlay.addEventListener('click', closePanel);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && panel.classList.contains('is-open')) closePanel();
      });
    }

    /* Dual-handle price slider: two range inputs grid-stacked into the same
       cell (only their thumbs are pointer-interactive, via
       ::-webkit/-moz-range-thumb — the track itself has pointer-events:none)
       sync live to the real number fields, which are what actually drive
       filtering, plus quick preset buttons that jump straight to a range. */
    document.querySelectorAll('[data-price-slider]').forEach(function (wrap) {
      var minThumb = wrap.querySelector('[data-price-thumb="min"]');
      var maxThumb = wrap.querySelector('[data-price-thumb="max"]');
      var minField = wrap.querySelector('[data-price-input="min"]');
      var maxField = wrap.querySelector('[data-price-input="max"]');
      var fill = wrap.querySelector('[data-price-fill]');
      var rangeMin = parseFloat(wrap.dataset.rangeMin);
      var rangeMax = parseFloat(wrap.dataset.rangeMax);
      var span = rangeMax - rangeMin || 1;

      function paint() {
        var minVal = parseFloat(minThumb.value);
        var maxVal = parseFloat(maxThumb.value);
        var leftPct = ((minVal - rangeMin) / span) * 100;
        var rightPct = ((maxVal - rangeMin) / span) * 100;
        fill.style.left = leftPct + '%';
        fill.style.width = Math.max(0, rightPct - leftPct) + '%';
      }

      function fromThumbs() {
        var minVal = Math.min(parseFloat(minThumb.value), parseFloat(maxThumb.value));
        var maxVal = Math.max(parseFloat(minThumb.value), parseFloat(maxThumb.value));
        minThumb.value = minVal;
        maxThumb.value = maxVal;
        minField.value = minVal;
        maxField.value = maxVal;
        paint();
      }

      function fromFields() {
        var minVal = minField.value === '' ? rangeMin : parseFloat(minField.value);
        var maxVal = maxField.value === '' ? rangeMax : parseFloat(maxField.value);
        minThumb.value = minVal;
        maxThumb.value = maxVal;
        paint();
        markActivePreset();
      }

      function setRange(minVal, maxVal) {
        minThumb.value = minVal;
        maxThumb.value = maxVal;
        minField.value = minVal;
        maxField.value = maxVal;
        paint();
        markActivePreset();
      }

      function markActivePreset() {
        var minVal = parseFloat(minThumb.value);
        var maxVal = parseFloat(maxThumb.value);
        wrap.querySelectorAll('[data-price-preset]').forEach(function (btn) {
          var pMin = parseFloat(btn.dataset.presetMin);
          var pMax = parseFloat(btn.dataset.presetMax);
          btn.classList.toggle('is-active', minVal === pMin && maxVal === pMax);
        });
      }

      minThumb.addEventListener('input', function () { fromThumbs(); markActivePreset(); });
      maxThumb.addEventListener('input', function () { fromThumbs(); markActivePreset(); });
      minField.addEventListener('input', fromFields);
      maxField.addEventListener('input', fromFields);
      wrap.querySelectorAll('[data-price-preset]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          setRange(btn.dataset.presetMin, btn.dataset.presetMax);
        });
      });

      paint();
      markActivePreset();
    });

    /* Every filter/sort control is staged in the form and only actually
       applied — one page load, not one per click — when "View Results"
       is pressed. The one exception is the color search box: that's a
       pure client-side narrowing of the swatch list, not a real filter,
       so it stays instant. */
    if (facetForm) {
      facetForm.addEventListener('input', function (e) {
        if (e.target.matches('[data-swatch-search]')) {
          var list = e.target.closest('.filter-group').querySelector('[data-swatch-list]');
          var term = e.target.value.trim().toLowerCase();
          list.querySelectorAll('[data-swatch-label]').forEach(function (label) {
            label.hidden = term.length > 0 && label.dataset.swatchLabel.indexOf(term) === -1;
          });
          return;
        }
        if (e.target.matches('[data-sort-select]')) {
          var current = document.querySelector('[data-sort-current]');
          if (current) current.textContent = e.target.options[e.target.selectedIndex].text;
        }
      });
    }

    var applyBtn = document.querySelector('[data-filters-apply]');
    if (applyBtn && facetForm) {
      applyBtn.addEventListener('click', function () { submitFilters(facetForm); });
    }

    function submitFilters(form) {
      var url = new URL(window.location.pathname, window.location.origin);
      new FormData(form).forEach(function (value, key) {
        if (value !== '') url.searchParams.append(key, value);
      });
      window.location.href = url.toString();
    }
  }

  /* ---------- Carousel arrow nav ----------
     Delegated at the document level (not bound per-node at init) so it also
     works for carousels injected later, e.g. the async-fetched product
     recommendations section. */
  function initCarouselNav() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-carousel-nav] button');
      if (!btn) return;
      var nav = btn.closest('[data-carousel-nav]');
      var target = document.getElementById(nav.dataset.carouselNav);
      if (!target) return;
      var amount = target.clientWidth * 0.8;
      target.scrollBy({
        left: btn.dataset.dir === 'next' ? amount : -amount,
        behavior: 'smooth'
      });
    });
  }

  /* ---------- Product card links: fix up the variant param at click time ----------
     Shopify's own Search & Discovery analytics decorates card links with
     _pos/_fid/_ss tracking params after render, and does it by naively
     re-appending whatever query string was already there behind a SECOND
     "?" instead of "&" — which corrupts a server-rendered ?variant= into
     a broken duplicate. Fixing the href here, at the moment of the click
     (after any such decoration has already run), sidesteps that
     regardless of what order the scripts happen to execute in. */
  function initPreselectVariantLinks() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-preselect-variant]');
      if (!link) return;
      var variantId = link.dataset.preselectVariant;
      if (!variantId) return;
      try {
        var url = new URL(link.href, window.location.origin);
        url.searchParams.delete('variant');
        url.searchParams.set('variant', variantId);
        link.href = url.pathname + '?' + url.searchParams.toString() + url.hash;
      } catch (err) {}
    });
  }

  /* ---------- Judge.me: our themed "Write a Review" button proxies their real hidden trigger ---------- */
  function initJudgemeWriteReview() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-jdgm-write-review]');
      if (!trigger) return;
      var widget = document.getElementById('judgeme_product_reviews');
      if (!widget) return;
      var real = widget.querySelector('.jm-action-buttons__button, .jdgm-write-rev-link');
      if (!real) {
        real = [].slice.call(widget.querySelectorAll('button, a')).find(function (el) {
          return /write a review/i.test(el.textContent);
        });
      }
      if (real) real.click();
    });
  }

  /* ---------- Reviews: shared helpers used by fetch, filters, sort, load-more ---------- */
  var CHECK_ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="4 12.5 9.5 18 20 6"/></svg>';

  function reviewEscapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Shows/hides .review-card elements inside a grid for a given filter,
     capping the "All" view to 6 unless already expanded via Show More —
     the one function every interaction (filter click, sort, load-more,
     and the initial post-fetch render) funnels through, so the cap and
     the empty-state message can never drift out of sync with each other. */
  function applyReviewFilter(grid, filter) {
    var expanded = grid.dataset.reviewExpanded === 'true';
    var cap = (filter === 'all' && !expanded) ? 6 : Infinity;
    var shown = 0, visible = 0;
    [].forEach.call(grid.querySelectorAll('.review-card'), function (card) {
      var matches = filter === 'all' ||
        (filter === 'photos' && card.dataset.hasPhotos === 'true') ||
        card.dataset.rating === filter;
      var show = matches && shown < cap;
      if (matches) shown++;
      card.hidden = !show;
      if (show) visible++;
    });
    var loadMoreBtn = document.querySelector('[data-review-load-more]');
    if (loadMoreBtn) loadMoreBtn.hidden = expanded || filter !== 'all' || shown <= 6;
    var empty = document.querySelector('[data-review-empty-state]');
    if (empty) empty.hidden = visible > 0;
  }

  /* ---------- Judge.me: fetch the FULL review set from their public widget endpoint.
     The metafield/injected snapshot is capped at Judge.me's "reviews per page" setting
     (max 10) — this is the same public, tokenless endpoint their own widget calls for
     pagination, with no such cap, so it replaces the server-rendered (capped) list once
     it loads. The SSR list stays as the instant/no-JS fallback. ---------- */
  function initJudgemeFullReviews() {
    var list = document.querySelector('.product-reviews__list[data-jdgm-product-id]');
    if (!list) return;
    var productId = list.dataset.jdgmProductId;
    var shopDomain = list.dataset.jdgmShopDomain;
    if (!productId || !shopDomain) return;

    function starsHtml(rating, size) {
      var percent = (rating / 5) * 100;
      return '<span class="review-stars review-stars--' + size + '"><span class="review-stars__icons" style="--rating-percent:' + percent + '%;">' +
        '<span class="review-stars__icons-bg">★★★★★</span>' +
        '<span class="review-stars__icons-fill">★★★★★</span></span></span>';
    }

    function cardHtml(r) {
      var name = r.is_anonymous_reviewer ? 'Anonymous' : reviewEscapeHtml(r.reviewer_name);
      var date = '';
      try {
        date = new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch (e) {}
      var verified = r.verified_buyer ? '<span class="review-card__verified">' + CHECK_ICON_SVG + 'Verified Buyer</span>' : '';
      var title = r.title ? '<h3 class="review-card__title">' + reviewEscapeHtml(r.title) + '</h3>' : '';
      var body = r.body ? '<p class="review-card__body">' + reviewEscapeHtml(r.body).replace(/\n/g, '<br>') + '</p>' : '';
      var hasPhotos = !!(r.pictures_urls && r.pictures_urls.length);
      var photos = hasPhotos ? '<div class="review-card__photos">' + r.pictures_urls.map(function (pic) {
        var thumb = pic.compact || pic.small || pic.original || pic.huge || '';
        var full = pic.huge || pic.original || thumb;
        return '<button type="button" class="review-card__photo-btn" data-review-photo="' + reviewEscapeHtml(full) + '">' +
          '<img src="' + reviewEscapeHtml(thumb) + '" loading="lazy" alt="Photo from ' + name + '’s review"></button>';
      }).join('') + '</div>' : '';
      return '<article class="review-card" data-rating="' + r.rating + '" data-has-photos="' + hasPhotos + '" data-created-at="' + reviewEscapeHtml(r.created_at) + '" data-helpful="' + (r.thumb_up || 0) + '">' +
        '<div class="review-card__head">' +
          '<span class="review-card__avatar" aria-hidden="true">' + reviewEscapeHtml(r.reviewer_initial) + '</span>' +
          '<div class="review-card__meta"><span class="review-card__name">' + name + '</span>' + verified + '</div>' +
          '<span class="review-card__date">' + date + '</span>' +
        '</div>' +
        starsHtml(r.rating, 'sm') + title + body + photos +
        '</article>';
    }

    function fetchPage(page) {
      var url = 'https://cdn.judge.me/reviews/reviews_for_widget?product_id=' + encodeURIComponent(productId) +
        '&page=' + page + '&per_page=50&sort_by=created_at&sort_dir=desc&skip_other_languages=true&shop_domain=' +
        encodeURIComponent(shopDomain) + '&platform=shopify';
      return fetch(url).then(function (res) { return res.ok ? res.json() : null; }).catch(function () { return null; });
    }

    fetchPage(1).then(function (data) {
      if (!data || !data.reviews || !data.reviews.length) return;
      var allReviews = data.reviews.slice();
      var totalPages = (data.pagination && data.pagination.total_pages) || 1;
      var more = [];
      for (var p = 2; p <= Math.min(totalPages, 5); p++) more.push(fetchPage(p));
      Promise.all(more).then(function (pages) {
        pages.forEach(function (d) { if (d && d.reviews) allReviews = allReviews.concat(d.reviews); });

        var filtersHtml = '<button type="button" class="review-filters__chip is-active" data-review-filter="all">All (' + allReviews.length + ')</button>';
        (data.histogram || []).forEach(function (row) {
          if (row.frequency > 0) {
            filtersHtml += '<button type="button" class="review-filters__chip" data-review-filter="' + row.rating + '">' + row.rating + '★ (' + row.frequency + ')</button>';
          }
        });
        var photoCount = allReviews.filter(function (r) { return r.pictures_urls && r.pictures_urls.length; }).length;
        if (photoCount > 0) {
          filtersHtml += '<button type="button" class="review-filters__chip" data-review-filter="photos">With Photos (' + photoCount + ')</button>';
        }
        var sortHtml = '<select class="filter-group__select product-reviews__sort" data-review-sort aria-label="Sort reviews">' +
          '<option value="recent">Most Recent</option><option value="rating_high">Highest Rated</option>' +
          '<option value="rating_low">Lowest Rated</option><option value="helpful">Most Helpful</option></select>';

        list.innerHTML = '<div class="product-reviews__toolbar"><div class="review-filters" role="group" aria-label="Filter reviews">' + filtersHtml + '</div>' + sortHtml + '</div>' +
          '<div class="review-card__grid" data-review-grid>' + allReviews.map(cardHtml).join('') + '</div>' +
          '<p class="review-card__empty-state" data-review-empty-state hidden>No reviews match this filter.</p>' +
          (allReviews.length > 6 ? '<button type="button" class="button button--outline product-reviews__load-more" data-review-load-more>Show More Reviews</button>' : '');

        list.dataset.reviewsData = JSON.stringify(allReviews.map(function (r) { return { rating: r.rating, created_at: r.created_at, thumb_up: r.thumb_up || 0 }; }));
        applyReviewFilter(list.querySelector('[data-review-grid]'), 'all');
      });
    });
  }

  /* ---------- Review rating / photo filters + load-more (client-side, over already-loaded reviews) ---------- */
  function initReviewFilters() {
    document.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-review-filter]');
      if (chip) {
        var group = chip.closest('.review-filters');
        var grid = document.querySelector('[data-review-grid]');
        if (!group || !grid) return;
        [].forEach.call(group.querySelectorAll('[data-review-filter]'), function (c) { c.classList.remove('is-active'); });
        chip.classList.add('is-active');
        applyReviewFilter(grid, chip.dataset.reviewFilter);
        return;
      }
      var loadMore = e.target.closest('[data-review-load-more]');
      if (loadMore) {
        var grid2 = document.querySelector('[data-review-grid]');
        if (!grid2) return;
        grid2.dataset.reviewExpanded = 'true';
        var activeChip = document.querySelector('.review-filters__chip.is-active');
        applyReviewFilter(grid2, activeChip ? activeChip.dataset.reviewFilter : 'all');
      }
    });
  }

  /* ---------- Review sort: re-orders the already-rendered .review-card elements in the DOM ---------- */
  function initReviewSort() {
    document.addEventListener('change', function (e) {
      var select = e.target.closest('[data-review-sort]');
      if (!select) return;
      var grid = document.querySelector('[data-review-grid]');
      if (!grid) return;
      var cards = [].slice.call(grid.querySelectorAll('.review-card'));
      var mode = select.value;
      cards.sort(function (a, b) {
        if (mode === 'rating_high') return b.dataset.rating - a.dataset.rating;
        if (mode === 'rating_low') return a.dataset.rating - b.dataset.rating;
        if (mode === 'helpful') return (b.dataset.helpful || 0) - (a.dataset.helpful || 0);
        return new Date(b.dataset.createdAt || 0) - new Date(a.dataset.createdAt || 0);
      });
      cards.forEach(function (card) { grid.appendChild(card); });
      var activeChip = document.querySelector('.review-filters__chip.is-active');
      applyReviewFilter(grid, activeChip ? activeChip.dataset.reviewFilter : 'all');
    });
  }

  /* ---------- Review photo lightbox ---------- */
  function initReviewLightbox() {
    var box = document.querySelector('[data-review-lightbox]');
    if (!box) return;
    var img = box.querySelector('[data-review-lightbox-img]');
    function close() { box.hidden = true; img.src = ''; }
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-review-photo]');
      if (trigger) {
        img.src = trigger.dataset.reviewPhoto;
        img.alt = trigger.querySelector('img') ? trigger.querySelector('img').alt : '';
        box.hidden = false;
        return;
      }
      if (e.target.closest('[data-review-lightbox-close]') || e.target === box) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !box.hidden) close();
    });
  }

  /* ---------- Product card colour swatches: jump the card slider ---------- */
  function initCardSwatches() {
    document.addEventListener('click', function (e) {
      var swatch = e.target.closest('[data-card-swatch]');
      if (!swatch) return;
      e.preventDefault();

      var card = swatch.closest('.product-card');
      var group = swatch.closest('[data-card-swatches]');
      if (group) {
        group.querySelectorAll('[data-card-swatch]').forEach(function (s) { s.classList.remove('is-active'); });
      }
      swatch.classList.add('is-active');

      if (!card) return;
      var position = swatch.dataset.swatchMediaPosition;
      if (!position || !card.goToCardMediaPosition) return;
      card.goToCardMediaPosition(position);
    });
  }

  /* ---------- Feature card shop swatches: jump slider + select the real variant to buy ---------- */
  function initShopSwatches() {
    document.addEventListener('click', function (e) {
      var swatch = e.target.closest('[data-shop-swatch]');
      if (!swatch) return;
      e.preventDefault();

      var card = swatch.closest('.product-card');
      var group = swatch.closest('[data-shop-swatches]');
      if (group) {
        group.querySelectorAll('[data-shop-swatch]').forEach(function (s) { s.classList.remove('is-active'); });
      }
      swatch.classList.add('is-active');

      if (!card) return;

      var variantInput = card.querySelector('[data-shop-variant-id]');
      if (variantInput && swatch.dataset.variantId) variantInput.value = swatch.dataset.variantId;

      var position = swatch.dataset.swatchMediaPosition;
      if (position && card.goToCardMediaPosition) card.goToCardMediaPosition(position);
    });
  }

  /* ---------- Feature grid cards: play the video once when it arrives on screen, then return to the main image ----------
     Plays through exactly one loop (loop is turned off just for this
     instance — the markup's `loop` attribute is for manual/carousel
     viewing elsewhere) and resets to the default slide on the video's own
     `ended` event rather than on any scroll-timing guess. Scrolling the
     card out of view and back does NOT replay it — `hasPlayed` is set the
     moment it finishes once, for good, for the rest of this page's life. */
  function initFeatureCardVideos() {
    if (!('IntersectionObserver' in window)) return;
    var cards = document.querySelectorAll('[data-card-feature]');
    if (!cards.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) playFeatureVideo(entry.target);
      });
    }, { threshold: 0.5 });

    cards.forEach(function (media) {
      var video = media.querySelector('[data-card-video]');
      if (!video) return;
      observer.observe(media);

      video.loop = false;
      video.addEventListener('ended', function () {
        video.dataset.hasPlayed = 'true';
        var card = media.closest('.product-card');
        if (card && card.goToCardSlide) card.goToCardSlide(0);
      });
    });

    function playFeatureVideo(media) {
      var card = media.closest('.product-card');
      var video = media.querySelector('[data-card-video]');
      if (!video || !card || video.dataset.hasPlayed === 'true') return;
      var slides = media.querySelectorAll('[data-card-slide]');
      var slide = video.closest('[data-card-slide]');
      var index = Array.prototype.indexOf.call(slides, slide);
      var activeIndex = Array.prototype.findIndex.call(slides, function (s) { return s.classList.contains('is-active'); });
      if (activeIndex === index) return; // already showing/playing this video — don't restart it mid-play
      if (index > 0 && card.goToCardSlide) {
        card.goToCardSlide(index);
      } else {
        video.currentTime = 0;
        playVideoWhenVisible(video);
      }
    }
  }

  /* ---------- Product card media slider: images + video, dots/arrows/swipe ----------
     Accepts an optional root so it can be re-run, scoped to just the new nodes,
     after content is injected later (e.g. async product recommendations) —
     without this, dynamically-injected cards never get goToCardSlide/nav/swipe. */
  function initCardSliders(root) {
    (root || document).querySelectorAll('[data-card-slider]').forEach(function (media) {
      var card = media.closest('.product-card');
      var slides = media.querySelectorAll('[data-card-slide]');
      var dots = media.querySelectorAll('[data-card-dots] .product-card__dot');
      if (!card || slides.length < 2) return;

      var current = 0;

      var slidesWrap = media.querySelector('[data-card-slides]');

      function goTo(index) {
        if (index < 0) index = slides.length - 1;
        if (index >= slides.length) index = 0;
        if (index === current) return;

        var prevVideo = slides[current].querySelector('[data-card-video]');
        if (prevVideo) prevVideo.pause();

        slides[current].classList.remove('is-active');
        if (dots[current]) dots[current].classList.remove('is-active');

        current = index;

        slides[current].classList.add('is-active');
        if (dots[current]) dots[current].classList.add('is-active');
        if (slidesWrap) slidesWrap.style.transform = 'translateX(-' + (current * 100) + '%)';

        var nextVideo = slides[current].querySelector('[data-card-video]');
        if (nextVideo) {
          nextVideo.currentTime = 0;
          playVideoWhenVisible(nextVideo);
        }
      }
      card.goToCardSlide = goTo;

      /* Whatever left this card on a non-default slide — a swatch click, a
         hover preview, the next/prev arrows — reset it back to the first
         photo once the shopper has scrolled past it. Without this, scrolling
         back to a card later (or just continuing to browse) can show a
         stale, non-default image instead of the card's real hero shot. */
      if ('IntersectionObserver' in window) {
        var resetObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) goTo(0);
          });
        }, { threshold: 0 });
        resetObserver.observe(media);
      }

      /* Sound is off by default (muted) on every card video — click the badge
         to turn it on for that one video. Doesn't affect other cards/slides. */
      media.querySelectorAll('[data-card-mute]').forEach(function (btn) {
        var video = btn.closest('.product-card__slide').querySelector('[data-card-video]');
        if (!video) return;
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          video.muted = !video.muted;
          btn.classList.toggle('is-unmuted', !video.muted);
          btn.setAttribute('aria-pressed', String(!video.muted));
          btn.setAttribute('aria-label', video.muted ? 'Turn video sound on' : 'Turn video sound off');
        });
      });

      /* Swatch clicks jump by media POSITION (from the product's full media
         list), not by array index — slides here are a curated subset, so the
         Nth rendered slide's index doesn't equal its position - 1. Search by
         the position each slide actually carries, same approach as the main
         PDP gallery's goToMediaPosition. */
      card.goToCardMediaPosition = function (position) {
        if (position == null) return false;
        var targetIndex = -1;
        Array.prototype.forEach.call(slides, function (s, i) {
          if (String(s.dataset.mediaPosition) === String(position)) targetIndex = i;
        });
        if (targetIndex === -1) return false;
        goTo(targetIndex);
        return true;
      };

      var prevBtn = media.querySelector('[data-card-nav="prev"]');
      var nextBtn = media.querySelector('[data-card-nav="next"]');
      if (prevBtn) prevBtn.addEventListener('click', function (e) { e.preventDefault(); goTo(current - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function (e) { e.preventDefault(); goTo(current + 1); });

      var dotsWrap = media.querySelector('[data-card-dots]');
      if (dotsWrap) {
        Array.prototype.forEach.call(dotsWrap.children, function (dot, i) {
          dot.addEventListener('click', function (e) { e.preventDefault(); goTo(i); });
        });
      }

      var link = media.querySelector('.product-card__link');
      var startX = 0, startY = 0, dragging = false, swiped = false;

      /* Inside a horizontally-scrolling carousel (e.g. product recommendations),
         a touch-drag on the card belongs to the OUTER track's own native scroll —
         binding the card's own swipe-to-change-image here would fight it and the
         whole strip visibly slides instead of the card's photo changing. */
      var inHorizontalCarousel = !!media.closest('.product-recommendations__track');

      if (slidesWrap && !inHorizontalCarousel) {
        slidesWrap.addEventListener('touchstart', function (e) {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          dragging = true;
          swiped = false;
        }, { passive: true });

        slidesWrap.addEventListener('touchmove', function (e) {
          if (!dragging) return;
          var dx = e.touches[0].clientX - startX;
          var dy = e.touches[0].clientY - startY;
          if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) swiped = true;
        }, { passive: true });

        slidesWrap.addEventListener('touchend', function (e) {
          if (!dragging) return;
          dragging = false;
          var dx = e.changedTouches[0].clientX - startX;
          var dy = e.changedTouches[0].clientY - startY;
          /* Require the gesture to have actually been horizontal (swiped, set
             during touchmove) AND still be horizontal-dominant at release —
             a vertical page scroll that merely ends with some net sideways
             drift from its start point must never change the slide. */
          if (swiped && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            goTo(dx < 0 ? current + 1 : current - 1);
          }
        });

        if (link) {
          link.addEventListener('click', function (e) {
            if (swiped) { e.preventDefault(); swiped = false; }
          });
        }
      }

      /* Desktop: hovering the card previews the 2nd image; leaving returns to the 1st.
         Only auto-reverts if the shopper hasn't manually navigated elsewhere.
         Debounced — while wheel-scrolling, a stationary cursor passes over
         several cards as the page moves under it, firing real mouseenter/
         mouseleave events for each one; a brief delay means only a genuine,
         resting hover (not a scroll-through) triggers the preview. */
      var hoverPreview = false;
      var hoverTimer = null;
      var canHover = window.matchMedia('(hover: hover)').matches;
      if (canHover) {
        media.addEventListener('mouseenter', function () {
          hoverTimer = setTimeout(function () {
            if (current === 0) { hoverPreview = true; goTo(1); }
          }, 150);
        });
        media.addEventListener('mouseleave', function () {
          clearTimeout(hoverTimer);
          if (hoverPreview && current === 1) goTo(0);
          hoverPreview = false;
        });
      }
    });
  }

  /* ---------- Footer country/currency selector ---------- */
  function initLocalizationSelect() {
    document.querySelectorAll('[data-localization-select]').forEach(function (select) {
      select.addEventListener('change', function () {
        if (select.form) select.form.submit();
      });
    });
  }

  /* ---------- Welcome discount popup ---------- */
  var WELCOME_SEEN_KEY = 'plushington_welcome_seen';   // permanent: popup has been shown/dismissed once, ever
  var WELCOME_JOINED_KEY = 'plushington_welcome_joined'; // permanent: visitor already has the code

  function initWelcomePopup() {
    var popup = document.querySelector('[data-welcome-popup]');
    var overlay = document.querySelector('[data-welcome-overlay]');
    var tab = document.querySelector('[data-welcome-tab]');
    if (!popup || !overlay) return;

    if (popup.hasAttribute('data-customer-logged-in')) return;

    var timer;
    var defaultPanel = popup.querySelector('[data-welcome-panel-default]');
    var successPanel = popup.querySelector('[data-welcome-panel-success]');

    var hasSeen = false;
    var hasJoined = false;
    try {
      hasSeen = localStorage.getItem(WELCOME_SEEN_KEY) === '1';
      hasJoined = localStorage.getItem(WELCOME_JOINED_KEY) === '1';
    } catch (e) {}

    var forceShow = window.location.search.indexOf('welcome=1') > -1;

    function markSeen() {
      try { localStorage.setItem(WELCOME_SEEN_KEY, '1'); } catch (e) {}
    }

    function showTab() {
      if (tab) tab.removeAttribute('hidden');
    }

    function hideTab() {
      if (tab) tab.setAttribute('hidden', '');
    }

    function showPopup(showSuccess) {
      hideTab();
      if (showSuccess) {
        if (defaultPanel) defaultPanel.setAttribute('hidden', '');
        if (successPanel) successPanel.removeAttribute('hidden');
      } else {
        if (defaultPanel) defaultPanel.removeAttribute('hidden');
        if (successPanel) successPanel.setAttribute('hidden', '');
      }
      overlay.removeAttribute('hidden');
      popup.removeAttribute('hidden');
      void popup.offsetWidth;
      popup.classList.add('is-visible');
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    function hidePopup() {
      if (typeof timer !== 'undefined') clearTimeout(timer);
      document.removeEventListener('mouseleave', onMouseLeave);
      popup.classList.remove('is-visible');
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      markSeen();
      setTimeout(function () {
        popup.setAttribute('hidden', '');
        overlay.setAttribute('hidden', '');
        // Offer the code again via the side tab instead of vanishing entirely
        showTab();
      }, 350);
    }

    popup.querySelectorAll('[data-welcome-close]').forEach(function (btn) {
      btn.addEventListener('click', hidePopup);
    });
    overlay.addEventListener('click', hidePopup);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && popup.classList.contains('is-visible')) {
        hidePopup();
      }
    });

    if (tab) {
      tab.addEventListener('click', function () {
        showPopup(hasJoined);
      });
    }

    // Copy coupon code
    var copyBtn = popup.querySelector('[data-welcome-copy]');
    var codeEl = popup.querySelector('[data-welcome-code]');
    if (copyBtn && codeEl) {
      copyBtn.addEventListener('click', function () {
        var code = codeEl.textContent.trim();
        if (navigator.clipboard) {
          navigator.clipboard.writeText(code).then(function () {
            copyBtn.textContent = 'Copied!';
            setTimeout(function () {
              copyBtn.textContent = 'Copy';
            }, 2500);
          });
        }
      });
    }

    // The form submits natively (real POST, full-page redirect back to this
    // same page via return_to) — Shopify's own validated pipeline, same as
    // the footer/homepage forms. A prior fetch()-based version could show a
    // fake success screen on a blocked or failed request without ever
    // creating the customer; native submission can't lie about that.
    // Did we just land back here after a real submission? Liquid sets this
    // attribute when form.posted_successfully? is true server-side.
    var justSucceeded = popup.hasAttribute('data-welcome-just-succeeded');

    if (justSucceeded) {
      hasJoined = true;
      markSeen();
      try { localStorage.setItem(WELCOME_JOINED_KEY, '1'); } catch (e) {}
      notifyOtherNewsletterForms();
      showPopup(true);
      // Clean the redirect marker out of the URL without another navigation
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.delete('welcome_joined');
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      }
      return;
    }

    // Already subscribed (via this popup, the footer, or the homepage form) —
    // never nag again, just leave the retrieval tab available.
    if (hasJoined) {
      showTab();
      return;
    }

    if (!forceShow && hasSeen) {
      showTab();
      return;
    }

    timer = setTimeout(function () {
      showPopup(false);
    }, 3000);

    function onMouseLeave(e) {
      if (e.clientY <= 0) {
        clearTimeout(timer);
        document.removeEventListener('mouseleave', onMouseLeave);
        showPopup(false);
      }
    }
    document.addEventListener('mouseleave', onMouseLeave);
  }

  /* ---------- Shared AJAX newsletter forms (footer + homepage sections) ---------- */
  function notifyOtherNewsletterForms() {
    try { localStorage.setItem(WELCOME_JOINED_KEY, '1'); } catch (e) {}
    var tab = document.querySelector('[data-welcome-tab]');
    if (tab) tab.removeAttribute('hidden');
  }

  // The footer and homepage newsletter forms submit natively (real POST,
  // full-page redirect) — Shopify's own validated pipeline, guaranteed to
  // create the customer record. A prior fetch()-based AJAX version silently
  // showed "success" even when the request failed or was blocked, without
  // ever creating a customer. Reliability over reload-avoidance here.
  // This just syncs the welcome-popup's "already joined" state after that
  // redirect completes, using the server-rendered success markup as the signal.
  function syncNewsletterJoinedFromServerState() {
    var success = document.querySelector('.site-footer__form-success, .newsletter-form__success');
    if (success) notifyOtherNewsletterForms();
  }

  /* ---------- PDP Size & Drape Guide Modal ---------- */
  function initSizeGuideModal() {
    var modal = document.querySelector('[data-size-guide-modal]');
    var overlay = document.querySelector('[data-size-guide-overlay]');
    var openBtn = document.querySelector('[data-size-guide-open]');
    if (!modal || !overlay || !openBtn) return;

    function openModal() {
      overlay.removeAttribute('hidden');
      modal.removeAttribute('hidden');
      void modal.offsetWidth;
      modal.classList.add('is-visible');
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      modal.classList.remove('is-visible');
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      setTimeout(function () {
        modal.setAttribute('hidden', '');
        overlay.setAttribute('hidden', '');
      }, 350);
    }

    openBtn.addEventListener('click', openModal);

    modal.querySelectorAll('[data-size-guide-close]').forEach(function (btn) {
      btn.addEventListener('click', closeModal);
    });
    overlay.addEventListener('click', closeModal);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-visible')) {
        closeModal();
      }
    });
  }
})();

